import { memo, useState, useMemo, useEffect } from 'react'
import BaseModal from '@/components/BaseModal/BaseModal'
import { Input, Collapse } from 'antd'
const { Search } = Input
import type { GetProps } from 'antd'
type SearchProps = GetProps<typeof Input.Search>
import { items } from './const'
import './PlatformModal.less'

// 模糊搜索函数
const fuzzySearch = (text: string, keyword: string): boolean => {
  if (!keyword) return true
  const textLower = text.toLowerCase()
  const keywordLower = keyword.toLowerCase()
  // 完全匹配
  if (textLower.includes(keywordLower)) return true
  // 模糊匹配：检查关键词的每个字符是否按顺序出现在文本中
  let textIndex = 0
  for (let i = 0; i < keywordLower.length; i++) {
    const char = keywordLower[i]
    const foundIndex = textLower.indexOf(char, textIndex)
    if (foundIndex === -1) return false
    textIndex = foundIndex + 1
  }
  return true
}
// 提取文本内容的辅助函数
const extractTextContent = (children: any): string => {
  if (typeof children === 'string') {
    return children
  }
  if (Array.isArray(children)) {
    return children.map(extractTextContent).join(' ')
  }
  if (children?.props?.children) {
    return extractTextContent(children.props.children)
  }
  return ''
}

// 高亮关键词的函数
const highlightKeyword = (text: string, keyword: string): React.ReactNode => {
  if (!keyword.trim()) return text

  const keywordLower = keyword.toLowerCase()
  const textLower = text.toLowerCase()
  const parts = []
  let lastIndex = 0

  // 找到所有匹配的位置
  let matchIndex = textLower.indexOf(keywordLower, lastIndex)
  while (matchIndex !== -1) {
    // 添加匹配前的文本
    if (matchIndex > lastIndex) {
      parts.push(text.slice(lastIndex, matchIndex))
    }
    // 添加高亮的匹配文本
    parts.push(
      <span key={matchIndex} className='highlight-keyword'>
        {text.slice(matchIndex, matchIndex + keyword.length)}
      </span>
    )
    lastIndex = matchIndex + keyword.length
    matchIndex = textLower.indexOf(keywordLower, lastIndex)
  }

  // 添加剩余的文本
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length > 0 ? parts : text
}

// 高亮渲染内容的函数
const renderHighlightedContent = (children: any, keyword: string): React.ReactNode => {
  if (typeof children === 'string') {
    return highlightKeyword(children, keyword)
  }
  if (Array.isArray(children)) {
    return children.map((child, index) => <span key={index}>{renderHighlightedContent(child, keyword)}</span>)
  }
  if (children?.props?.children) {
    return {
      ...children,
      props: {
        ...children.props,
        children: renderHighlightedContent(children.props.children, keyword)
      }
    }
  }
  return children
}
const PlatformModal = ({ visible, onCancel, onOk }: any) => {
  const [searchKeyword, setSearchKeyword] = useState('')
  const [activeKeys, setActiveKeys] = useState<string[]>([])
  // 根据搜索关键词过滤数据
  const filteredList = useMemo(() => {
    if (!searchKeyword.trim()) {
      return items
    }
    const keyword = searchKeyword.trim()
    return items
      .filter(item => {
        // 搜索标题 - 支持模糊搜索
        const titleMatch = fuzzySearch(item.label, keyword)
        // 搜索内容 - 支持模糊搜索
        const contentText = extractTextContent(item.children)
        const contentMatch = fuzzySearch(contentText, keyword)
        return titleMatch || contentMatch
      })
      .map(item => ({
        ...item,
        // 高亮标题
        label: highlightKeyword(item.label, keyword),
        // 高亮内容
        children: renderHighlightedContent(item.children, keyword)
      }))
  }, [searchKeyword])
  // 当搜索结果变化时，自动打开匹配的项目
  useEffect(() => {
    if (searchKeyword.trim() && filteredList.length > 0) {
      const keysToOpen = filteredList.map(item => item.key)
      setActiveKeys(keysToOpen)
    } else if (!searchKeyword.trim()) {
      setActiveKeys([])
    }
  }, [filteredList, searchKeyword])
  const onChange = (key: string | string[]) => {
    setActiveKeys(Array.isArray(key) ? key : [key])
  }
  const onSearch: SearchProps['onSearch'] = value => {
    setSearchKeyword(value)
  }
  return (
    <BaseModal modalProps={{ open: visible, onCancel, onOk }} title={null} className='base-modal-bg-gradien platformModal'>
      <div className='platform-nav'>
        <h4>文果使用说明</h4>
        <Search placeholder='搜索关键词' allowClear enterButton='搜索' onSearch={onSearch} />
      </div>
      <div className='base-modal-section'>
        <Collapse items={filteredList} ghost={true} onChange={onChange} accordion={false} activeKey={activeKeys} />
      </div>
    </BaseModal>
  )
}

export default memo(PlatformModal)
